import { ChangeDetectionStrategy, Component, computed, effect, inject, input, signal } from '@angular/core';
import { DomSanitizer, SafeResourceUrl } from '@angular/platform-browser';

import { EventModel } from '@frontend/sports/betting-offer/feature/model';
import { trackingConstants } from '@frontend/sports/tracking/feature';
import { DsAlert } from '@frontend/ui/alert';
import { Store } from '@ngrx/store';

import { OptionGroupTrackingService } from '../option-group-tracking.service';
import { setPlayerStatisticsOpenAndTrackingStatus } from './player-statistics.actions';
import { PlayerStatisticMessages, PlayerStatisticsStoreData, StatisticsInput } from './player-statistics.model';
import { PlayerStatisticsService } from './player-statistics.service';

@Component({
    selector: 'ms-player-statistics',
    styleUrl: 'player-statistics.component.scss',
    templateUrl: './player-statistics.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [DsAlert],
})
export class PlayerStatisticsComponent {
    private optionGroupTrackingService = inject(OptionGroupTrackingService);
    event = input.required<EventModel>();
    marketFilter = input.required<string>();
    playerName = input.required<string>();
    tracking = input.required<string>();
    isSheetViewOpen = input<boolean>();
    optionGroupName = input<string>();
    marketId = input<string>();

    statistics = input<PlayerStatisticsStoreData>();
    messages = input<PlayerStatisticMessages>();
    isIframeLoading = signal(true);
    safeUrl = signal<SafeResourceUrl | undefined>(undefined);

    sanitizer = inject(DomSanitizer);
    playerStatisticsService = inject(PlayerStatisticsService);
    store = inject(Store);
    private isReady = computed(() => {
        const storeData = this.statistics();
        if (!storeData) {
            return false;
        }
        const { isLoading, isError, data } = storeData;
        return !isLoading && !isError && !!data && !!this.event() && !!this.playerName();
    });

    constructor() {
        effect(() => {
            const statisticsInput = {
                eventId: this.event().id,
                marketId: this.marketId(),
                playerName: this.playerName(),
            };
            const storeData = this.statistics();
            const { isLoading, isError, data } = storeData!;

            const statisticsData = this.playerStatisticsService.getPlayerStatistics(data, this.marketId()!, this.playerName());

            if (!statisticsData?.isOpen) {
                return;
            }
            if (!statisticsData?.tracking) {
                this.sendTrackingEvent({
                    actionEvent: trackingConstants.EVENT_CLICK,
                    event: trackingConstants.EVENT_TRACKING,
                    eventDetailText: statisticsInput.playerName,
                });
            }
            if (!isLoading && isError && !statisticsData?.tracking) {
                this.sendTrackingEvent({
                    actionEvent: trackingConstants.LOAD,
                    event: trackingConstants.EVENT_CONTENT_VIEW,
                    eventDetailText: `${statisticsInput.playerName}-${trackingConstants.STATS_NOT_LOADING}`,
                });

                this.updatePlayerStatisticsStatusStore(statisticsInput, { tracking: true });
            }

            if (this.isReady() && storeData && statisticsInput) {
                const graphUrl = this.playerStatisticsService.getPlayerStatisticsGraph(data, statisticsInput);
                if (graphUrl) {
                    if (!statisticsData?.tracking) {
                        this.sendTrackingEvent({
                            actionEvent: trackingConstants.LOAD,
                            event: trackingConstants.EVENT_CONTENT_VIEW,
                            eventDetailText: statisticsInput.playerName,
                        });
                        this.updatePlayerStatisticsStatusStore(statisticsInput, { tracking: true });
                    }
                    const safeUrl = this.safeUrl();
                    if (!safeUrl) {
                        this.safeUrl.set(this.sanitizer.bypassSecurityTrustResourceUrl(graphUrl));
                        this.isIframeLoading.set(true);
                    }
                    return;
                }

                if (!statisticsData?.tracking) {
                    this.sendTrackingEvent({
                        actionEvent: trackingConstants.LOAD,
                        event: trackingConstants.EVENT_CONTENT_VIEW,
                        eventDetailText: `${statisticsInput.playerName}-${trackingConstants.STATS_NOT_LOADING}`,
                    });
                    this.updatePlayerStatisticsStatusStore(statisticsInput, { tracking: true });
                }
            }

            this.safeUrl.set(undefined);
            this.isIframeLoading.set(false);
        });
    }

    onIframeLoad(): void {
        this.isIframeLoading.set(false);
    }

    private getLocationEvent(): string {
        return this.isSheetViewOpen() ? trackingConstants.SEE_ALL_OVERLAY : (this.tracking().split('/')[0] ?? '');
    }

    private sendTrackingEvent(params: { actionEvent: string; event: string; eventDetailText: string }): void {
        const event = this.event();
        this.optionGroupTrackingService.trackEvents({
            label: trackingConstants.PLAYER_STATS,
            actionEvent: params.actionEvent,
            participants: [],
            locationEvent: `${this.getLocationEvent()}-${this.optionGroupName()}`,
            eventDetailText: params.eventDetailText,
            sportId: event.sport.id,
            leagueId: event.league.id,
            event: params.event,
            marketId: this.marketId(),
            eventId: event.id,
            categoryType: trackingConstants.SCOREBOARD_EVENT,
            positionEvent: event.live ? trackingConstants.EVENT_LIVE : trackingConstants.EVENT_PRELIVE,
        });
    }

    private updatePlayerStatisticsStatusStore(
        statisticsInput: StatisticsInput,
        storeStatisticsUpdate: { isOpen?: boolean; tracking?: boolean },
    ): void {
        const payload = {
            eventId: statisticsInput.eventId,
            marketFilter: this.marketFilter(),
            mainMarket: false,
            playerName: statisticsInput.playerName,
            marketId: statisticsInput.marketId ?? '',
            ...storeStatisticsUpdate,
        };
        this.store.dispatch(setPlayerStatisticsOpenAndTrackingStatus(payload));
    }
}
